/*
    Copyright (c) Computer-L.A.N. GmbH (http://www.computer-lan.de).
    Alle Rechte vorbehalten.
*/

import React from "react";
import { Box, BoxProps, Checkbox, Stack, Tab, TableCell, Tabs } from "@mui/material";
import DragAndDropTable, { DragAndDropTableBody, DragAndDropTableHead } from "../../../../Basis/Components/DragAndDropTable";
import StyledBadge from "../../../../Basis/Components/StyledBadge";
import { AuswahlService } from "../../../../Basis/Utils/useAuswahl";
import Filter from "../../../DomainDrivenDesign/Commands/Filter";
import FilterTable from "../../../DomainDrivenDesign/Components/FilterTable";
import FilterInfo from "../../../DomainDrivenDesign/Queries/FilterInfo";
import Feld from "../../../Exporte/Queries/Feld";
import { translationKeys, useTranslation } from "../../Internationalisierung/i18nExporte";

export const TABS = { SPALTEN: "spalten", FILTER: "filter" } as const;

export type TabValue = typeof TABS[keyof typeof TABS];

function a11yProps(tabValue: TabValue) {
    return {
        id: `tab-${tabValue}`,
        "aria-controls": `tabpanel-${tabValue}`,
    };
}

interface TabPanelProps {
    children?: React.ReactNode;
    value: TabValue;
    panel: TabValue;
    sx: BoxProps["sx"];
}

function TabPanel(props: TabPanelProps) {
    return (
        <Box
            role="tabpanel"
            hidden={props.value !== props.panel}
            id={`tabpanel-${props.panel}`}
            aria-labelledby={`tab-${props.panel}`}
            sx={props.sx}
        >
            {props.panel === props.value && (
                props.children
            )}
        </Box>
    );
}

interface SpaltenTableProps<TFeld extends Feld> {
    felder: TFeld[];
    onSortieren: (drag: TFeld, drop: TFeld) => void;
    auswahl: SpaltenAuswahl<TFeld>;
    sortieren: boolean;
}

function SpaltenTable<TFeld extends Feld>(props: SpaltenTableProps<TFeld>) {
    const { t } = useTranslation();
    const { felder, onSortieren, auswahl, sortieren } = props;
    return (
        <DragAndDropTable
            dnd={sortieren}
            variant="outlined"
            data={felder}
            head={(
                <DragAndDropTableHead>
                    <TableCell padding="checkbox">
                        <Checkbox
                            checked={auswahl.alleAusgewaehlt}
                            onChange={(_, checked) => {
                                auswahl.aendereAuswahlFuerAlle(checked);
                            }}
                            indeterminate={auswahl.unbestimmteAuswahl}
                        />
                    </TableCell>
                    <TableCell>
                        {t(translationKeys.spaltenTable.spalte)}
                    </TableCell>
                </DragAndDropTableHead>
            )}
            renderBody={feld => (
                <DragAndDropTableBody>
                    <TableCell padding="checkbox">
                        <Checkbox
                            checked={auswahl.istAusgewaehlt(feld)}
                            onChange={(_, checked) => {
                                auswahl.aendereAuswahl(feld, checked);
                            }}
                        />
                    </TableCell>
                    <TableCell>
                        {feld.name}
                    </TableCell>
                </DragAndDropTableBody>
            )}
            onDrop={onSortieren}
        />
    );
}

export type SpaltenAuswahl<TFeld extends Feld> = Pick<AuswahlService<TFeld>, "alleAusgewaehlt" | "aendereAuswahlFuerAlle" | "unbestimmteAuswahl" | "istAusgewaehlt" | "aendereAuswahl" | "ausgewaehlteElemente">;

export interface SpaltenUndFilterViewProps<TFeld extends Feld, TFilter extends Filter> {
    tabValue: TabValue;
    setTabValue: React.Dispatch<React.SetStateAction<TabValue>>;
    felder: TFeld[];
    onSortieren: (drag: TFeld, drop: TFeld) => void;
    auswahl: SpaltenAuswahl<TFeld>;
    filter: TFilter[];
    setFilter: React.Dispatch<React.SetStateAction<TFilter[]>>;
    findeFilterInfosFuerCsvExport: (suchbegriff: string) => Promise<FilterInfo[]>;
    commandName: string;
    sortieren: boolean;
    pfadZuFilter?: string[];
}

export default function SpaltenUndFilterView<TFeld extends Feld, TFilter extends Filter>(props: SpaltenUndFilterViewProps<TFeld, TFilter>) {
    const { t } = useTranslation();
    const { tabValue, setTabValue, felder, onSortieren, auswahl, filter, setFilter, findeFilterInfosFuerCsvExport, commandName, sortieren } = props;
    return (
        <Stack sx={{ overflow: "auto" }}>
            <Box>
                <Tabs value={tabValue}>
                    <Tab
                        label={<StyledBadge
                            badgeContent={auswahl.ausgewaehlteElemente.length}
                        >
                            {t(translationKeys.erstelleCsvExportDialog.spalten)}
                        </StyledBadge>}
                        value={TABS.SPALTEN}
                        onClick={setTabValue.bind(null, TABS.SPALTEN)}
                        {...a11yProps(TABS.SPALTEN)}
                    />
                    <Tab
                        label={<StyledBadge
                            badgeContent={filter.length}
                        >
                            {t(translationKeys.erstelleCsvExportDialog.filter)}
                        </StyledBadge>}
                        value={TABS.FILTER}
                        onClick={setTabValue.bind(null, TABS.FILTER)}
                        {...a11yProps(TABS.FILTER)}
                    />
                </Tabs>
            </Box>

            <TabPanel value={tabValue} panel={TABS.SPALTEN} sx={{ flex: 1, overflowY: "auto" }}>
                <SpaltenTable
                    felder={felder}
                    onSortieren={onSortieren}
                    auswahl={auswahl}
                    sortieren={sortieren}
                />
            </TabPanel>

            <TabPanel value={tabValue} panel={TABS.FILTER} sx={{ flex: 1, overflowY: "auto" }}>
                <FilterTable<TFilter>
                    filter={filter}
                    setFilter={setFilter}
                    findeFilterInfos={findeFilterInfosFuerCsvExport}
                    commandName={commandName}
                    pfadZuFilter={props.pfadZuFilter ?? []}
                />
            </TabPanel>
        </Stack>
    );
}
